"use client";

import { FC, useState, useRef, useCallback, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import { ExploreRelatedImage } from "@/types/thread";
import { useAppDispatch } from "@/redux/store";
import { setRoomUUID, setSelectedGeneration } from "@/redux/actions";
import useExploreRelated from "@/hooks/useExploreRelated";
import Masonry, { ResponsiveMasonry } from "@/components/layout/masonry";
import GalleryImage from "./GalleryImage";

interface ExploreGalleryProps {
  generationId: number;
  galleryImages: ExploreRelatedImage[];
  initialRemainingGenIds: number[];
}

const ExploreGallery: FC<ExploreGalleryProps> = ({
  generationId,
  galleryImages,
  initialRemainingGenIds,
}) => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const { images, loading, remainingGenIds, fetchMoreGens } = useExploreRelated(
    generationId,
    galleryImages,
    initialRemainingGenIds
  );

  const displayIncrement = isMobile ? 10 : 30;
  const [displayCount, setDisplayCount] = useState(displayIncrement);
  const displayedImages = images.slice(0, displayCount);
  const [approachingBottom, setApproachingBottom] = useState(false);
  const isLoadingMore = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.body.offsetHeight;
      const scrollThreshold = 0.8;

      if (scrollPosition > pageHeight * scrollThreshold) {
        setApproachingBottom(true);
      } else {
        setApproachingBottom(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (approachingBottom && !isLoadingMore.current) {
      if (images.length > displayCount) {
        setDisplayCount((prev) =>
          Math.min(prev + displayIncrement, images.length)
        );
      } else if (remainingGenIds.length > 0) {
        isLoadingMore.current = true;
        fetchMoreGens().finally(() => {
          isLoadingMore.current = false;
        });
      }
    }
  }, [
    approachingBottom,
    images.length,
    displayCount,
    remainingGenIds,
    fetchMoreGens,
  ]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || isLoadingMore.current || !node) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isLoadingMore.current) {
            if (images.length > displayCount) {
              requestAnimationFrame(() => {
                setDisplayCount((prev) =>
                  Math.min(prev + displayIncrement, images.length)
                );
              });
            } else if (remainingGenIds.length > 0) {
              isLoadingMore.current = true;
              fetchMoreGens().finally(() => {
                isLoadingMore.current = false;
              });
            }
          }
        },
        { rootMargin: "300px", threshold: 0.1 }
      );

      observer.current.observe(node);
    },
    [loading, images.length, displayCount, remainingGenIds, fetchMoreGens]
  );

  const handleImageClick = (id: number) => {
    dispatch(setRoomUUID(null));
    dispatch(setSelectedGeneration(id));
  };

  return (
    <>
      {!loading && images.length === 0 && (
        <p className={classes.placeholderText}>No related images found.</p>
      )}
      <ResponsiveMasonry
        columnsCountBreakpoints={{
          350: 2,
          800: 3,
          1000: 4,
          1200: 5,
          1500: 6,
          1900: 7,
        }}
      >
        <Masonry gutter="0.5em">
          {displayedImages.map((item, index) => (
            <GalleryImage
              key={item.id}
              data={item}
              onImageClick={handleImageClick}
              ref={index === displayedImages.length - 1 ? lastElementRef : null}
            />
          ))}
        </Masonry>
      </ResponsiveMasonry>
    </>
  );
};

export default ExploreGallery;
